import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import type { ZEElementType } from "../types";
import { OWNERSHIP_OPTIONS } from "../utils";

interface Props {
  elementTypes: ZEElementType[];
}

/** Read-only table: element label → three PricingPreset parameter keys derived from preset_key. */
export function ZEPresetKeyLegend({ elementTypes }: Props) {
  const [open, setOpen] = useState(false);
  const mapped = elementTypes.filter((t) => t.is_active && t.preset_key);
  if (mapped.length === 0) return null;

  const lines = mapped.filter((t) => t.kind === "line");
  const objects = mapped.filter((t) => t.kind === "object");

  return (
    <div className="mt-3 border border-gray-200 dark:border-gray-700 rounded">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50"
      >
        <span>Legenda kluczy presetowych</span>
        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
      </button>

      {open && (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] font-mono border-t border-gray-200 dark:border-gray-700">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400">
                <th className="text-left px-3 py-1.5 font-medium">Typ elementu</th>
                <th className="text-left px-3 py-1.5 font-medium">Klucz główny</th>
                <th className="text-left px-3 py-1.5 font-medium">Działki prywatne</th>
                <th className="text-left px-3 py-1.5 font-medium">WPW prywatne</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {lines.length > 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-1 text-[10px] font-sans font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide bg-gray-50/60 dark:bg-gray-800/30">
                    Linie
                  </td>
                </tr>
              )}
              {lines.map((t) => (
                <LegendRow key={t.id} type={t} />
              ))}
              {objects.length > 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-1 text-[10px] font-sans font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide bg-gray-50/60 dark:bg-gray-800/30">
                    Obiekty
                  </td>
                </tr>
              )}
              {objects.map((t) => (
                <LegendRow key={t.id} type={t} />
              ))}
            </tbody>
          </table>

          {/* Global per-parcel-kind counts (independent of element type). Emitted only for
              kinds present in the variant (count > 0); declare a matching preset parameter
              to use them in a formula. */}
          <table className="w-full text-[11px] font-mono border-t border-gray-200 dark:border-gray-700">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400">
                <th className="text-left px-3 py-1.5 font-medium">Rodzaj działki</th>
                <th className="text-left px-3 py-1.5 font-medium">Klucz (liczba działek)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              <tr>
                <td colSpan={2} className="px-3 py-1 text-[10px] font-sans font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide bg-gray-50/60 dark:bg-gray-800/30">
                  Rodzaje działek (globalnie, poza likwidacją)
                </td>
              </tr>
              {OWNERSHIP_OPTIONS.map((o) => (
                <tr key={o.value} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/20">
                  <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300 font-sans text-[11px]">{o.label}</td>
                  <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">ilosc_dz_{o.value}</td>
                </tr>
              ))}
              {/* Private parcels carrying ONLY liquidation (overhead line and/or pole) of
                  one voltage and nothing else. Always emitted (incl. 0). */}
              <tr>
                <td colSpan={2} className="px-3 py-1 text-[10px] font-sans font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide bg-gray-50/60 dark:bg-gray-800/30">
                  Działki prywatne tylko z likwidacją (linia napowietrzna i/lub słup, nic innego)
                </td>
              </tr>
              <tr className="hover:bg-gray-50/50 dark:hover:bg-gray-800/20">
                <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300 font-sans text-[11px]">Tylko likwidacja SN</td>
                <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">ilosc_dz_prv_tylko_likw_sn</td>
              </tr>
              <tr className="hover:bg-gray-50/50 dark:hover:bg-gray-800/20">
                <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300 font-sans text-[11px]">Tylko likwidacja nN</td>
                <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">ilosc_dz_prv_tylko_likw_nn</td>
              </tr>
              {/* Distinct neighbours (≥0.5 m shared boundary) of parcels marked as
                  "WPW Prywatny — droga wewn.", private or unknown ownership, split by
                  whether the neighbour is itself in the drawing. Always emitted (incl. 0). */}
              <tr>
                <td colSpan={2} className="px-3 py-1 text-[10px] font-sans font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide bg-gray-50/60 dark:bg-gray-800/30">
                  Działki prywatne ościenne dróg wewnętrznych (oznaczonych ręcznie)
                  <span className="block normal-case font-normal tracking-normal text-gray-400 dark:text-gray-500">
                    Liczą się dopiero po doliczeniu sąsiadów oznaczonych dróg — do tego czasu
                    oba klucze pokazują 0. Panel Działki i okno wyceny ostrzegają, gdy sąsiedzi
                    nie są policzeni.
                  </span>
                </td>
              </tr>
              <tr className="hover:bg-gray-50/50 dark:hover:bg-gray-800/20">
                <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300 font-sans text-[11px]">Ościenne — w rysunku (idziemy po nich)</td>
                <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">ilosc_dz_prv_osc_dr_wewn</td>
              </tr>
              <tr className="hover:bg-gray-50/50 dark:hover:bg-gray-800/20">
                <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300 font-sans text-[11px]">Ościenne — poza rysunkiem</td>
                <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">ilosc_dz_prv_osc_dr_wewn_poza</td>
              </tr>
              {/* Global boolean flags (0/1), emitted once the respective analysis has run. */}
              <tr>
                <td colSpan={2} className="px-3 py-1 text-[10px] font-sans font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide bg-gray-50/60 dark:bg-gray-800/30">
                  Flagi 0/1
                </td>
              </tr>
              <tr className="hover:bg-gray-50/50 dark:hover:bg-gray-800/20">
                <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300 font-sans text-[11px]">MPZP — KONIECZNE ULICP (0/1)</td>
                <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">mpzp_ulicp</td>
              </tr>
              <tr className="hover:bg-gray-50/50 dark:hover:bg-gray-800/20">
                <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300 font-sans text-[11px]">
                  Obszary chronione GDOŚ na trasie (0/1)
                  <span className="ml-1.5 text-gray-400 dark:text-gray-600 font-mono text-[10px]">
                    park narodowy / rezerwat / park krajobrazowy / Natura 2000
                  </span>
                </td>
                <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">obszar_chroniony</td>
              </tr>
              {/* Przejścia przez wody — LICZBY sztuk (nie flaga): każde przejście to
                  osobna pozycja cennika. Emitowane zawsze po analizie, także jako 0. */}
              <tr>
                <td colSpan={2} className="px-3 py-1 text-[10px] font-sans font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide bg-gray-50/60 dark:bg-gray-800/30">
                  Przejścia przez wody (Wody Polskie, z Hydroportalu)
                  <span className="block normal-case font-normal tracking-normal text-gray-400 dark:text-gray-500">
                    Liczba sztuk, po jednej na pozycję cennika. Liczą się po „Przelicz
                    działki"; rodzaj bierze się z przeciętej linii (istniejące nawiązania
                    nie tworzą przejścia).
                  </span>
                </td>
              </tr>
              <tr className="hover:bg-gray-50/50 dark:hover:bg-gray-800/20">
                <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300 font-sans text-[11px]">
                  Przejścia doziemne (kabel / przewiert)
                  <span className="ml-1.5 text-gray-400 dark:text-gray-600 font-mono text-[10px]">
                    poz. „Przejście WP - doziemne przejście"
                  </span>
                </td>
                <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">przejscie_wp_ziemne</td>
              </tr>
              <tr className="hover:bg-gray-50/50 dark:hover:bg-gray-800/20">
                <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300 font-sans text-[11px]">
                  Przejścia napowietrzne (linia nad ciekiem)
                  <span className="ml-1.5 text-gray-400 dark:text-gray-600 font-mono text-[10px]">
                    poz. „Przejście WP - napowietrzne przejście"
                  </span>
                </td>
                <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">przejscie_wp_napowietrzne</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LegendRow({ type: t }: { type: ZEElementType }) {
  return (
    <tr className="hover:bg-gray-50/50 dark:hover:bg-gray-800/20">
      <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300 font-sans text-[11px]">
        {t.label}
        <span className="ml-1.5 text-gray-400 dark:text-gray-600 font-mono text-[10px]">{t.unit}</span>
      </td>
      <td className="px-3 py-1.5 text-[var(--color-primary)]">{t.preset_key}</td>
      <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">{t.preset_key}_ilosc_dz_prv</td>
      <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">{t.preset_key}_ilosc_wpw_prv</td>
    </tr>
  );
}
